import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import type { UseMediaPlanDetails, BreadCrumbItems } from '../MediaPlanDetails.types';
import { NAMESPACE, PRIVATE_PATH } from '@/constants';
import {
  useGetCampaignListQuery,
  useGetChannelsBudgetQuery,
  useGetClientListQuery,
  useGetMediaPlanQuery,
  useGetUserListQuery
} from '@/api';
import { enrichMediaPlanDetails } from '@/api/mediaPlanMappers';

notification.config({ maxCount: 1 });

export const useMediaPlanDetails: UseMediaPlanDetails = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const { id = '' } = useParams<{ id: string }>();
  const { data: rawMediaPlan, isLoading: isMediaPlanLoading } = useGetMediaPlanQuery(id);
  const { data: channelsBudget } = useGetChannelsBudgetQuery({ mediaPlanId: id });
  const { data: clients } = useGetClientListQuery();
  const { data: userList } = useGetUserListQuery({ limit: 500, page: 1 });
  const clientId =
    typeof rawMediaPlan?.client === 'string'
      ? rawMediaPlan.client
      : rawMediaPlan?.client?._id;
  const { data: campaigns } = useGetCampaignListQuery(
    { client: clientId ?? '' },
    { skip: !clientId }
  );

  const mediaPlan = useMemo(
    () =>
      enrichMediaPlanDetails(rawMediaPlan, {
        clients,
        users: userList?.data,
        campaigns
      }),
    [rawMediaPlan, clients, userList?.data, campaigns]
  );

  const navigateToMediaPlanList = () => navigate(PRIVATE_PATH.MEDIA_PLAN);

  const breadCrumbItems: BreadCrumbItems = useMemo(
    () => [
      {
        onClick: (e) => {
          e.preventDefault();
          navigate(PRIVATE_PATH.MEDIA_PLAN);
        },
        href: '',
        title: t('mediaPlan')
      },
      { title: mediaPlan?.campaign?.name ?? rawMediaPlan?.campaign?.name }
    ],
    [mediaPlan?.campaign, rawMediaPlan?.campaign, navigate, t]
  );

  return {
    id,
    navigateToMediaPlanList,
    breadCrumbItems,
    channelsBudget,
    isMediaPlanLoading,
    mediaPlan
  };
};
